import {
  Container,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import React, { useContext, useEffect, useState } from "react";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import { toast } from "react-toastify";
import AccountSetup from "../AccountSetup";
import { UserContext } from "../../context/UserProvider";

const Settings = () => {
  // State for designations
  const { user, loading, reloadUser } = useContext(UserContext);
  const [designation, setDesignation] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [apiDesignationList, setApiDesignationList] = useState([]);

  // State for visitor time
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  // State for auto approve
  const [autoApprove, setAutoApprove] = useState(false);

  useEffect(() => {
    if (user) {
      setStartTime(user?.visitorStartTime);
      setEndTime(user?.visitorEndTime);
      setAutoApprove(user?.autoApprove);
    }
  }, [user]);

  // Fetch designations from API
  const fetchDesignations = async () => {
    try {
      const response = await fetch(`${API_ENDPOINT}admin/get-designations`, {
        method: "GET",
        ...getHeader(),
      });
      if (response.ok) {
        const data = await response.json();
        setApiDesignationList(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to fetch designations");
      }
    } catch (error) {
      toast.error("Error fetching designations: " + error.message);
    }
  };

  // Handle adding designation
  const handleAddDesignation = async () => {
    if (designation.trim() === "") {
      toast.warn("Please enter a designation");
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINT}admin/add-designation`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({ designation }),
      });

      const data = await response.json();
      if (response.ok) {
        setDesignation("");
        fetchDesignations(); // Refresh the dialog list if open
        toast.success(data.message || "Designation added successfully");
      } else {
        toast.error(data.message || "Failed to add designation");
      }
    } catch (error) {
      toast.error("Error adding designation: " + error.message);
    }
  };

  // Handle deleting designation
  const handleDeleteDesignation = async (designationToDelete) => {
    try {
      const response = await fetch(
        `${API_ENDPOINT}admin/delete-designation?designation=${designationToDelete}`,
        {
          method: "DELETE",
          ...getHeader(),
        }
      );

      if (response.ok) {
        fetchDesignations();
        toast.success("Designation deleted successfully");
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to delete designation");
      }
    } catch (error) {
      toast.error("Error deleting designation: " + error.message);
    }
  };

  // Handle time settings submission
  const handleTimeSubmit = async () => {
    if (!startTime || !endTime) {
      toast.warn("Please set both start and end times");
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINT}org/set-visitor-time`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({ startTime, endTime }),
      });

      if (response.ok) {
        toast.success("Time settings saved successfully");
        reloadUser();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to save time settings");
      }
    } catch (error) {
      toast.error("Error saving time settings: " + error.message);
    }
  };

  // Handle auto approve toggle
  const handleAutoApproveChange = async (event) => {
    const newValue = event.target.checked;
    setAutoApprove(newValue);

    try {
      const response = await fetch(`${API_ENDPOINT}org/set-auto-approve`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({ autoApprove: newValue }),
      });

      if (response.ok) {
        toast.success(`Auto approve ${newValue ? "enabled" : "disabled"}`);
        reloadUser();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to update auto approve");
      }
    } catch (error) {
      toast.error("Error updating auto approve: " + error.message);
    }
  };

  // Open dialog and fetch designations
  const handleOpenDialog = () => {
    fetchDesignations();
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom color="primary">
        Settings
      </Typography>

      <AccountSetup />

      <Grid container spacing={3}>
        {/* Designation Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="text.primary">
              Designations
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Add Designation"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              />
              <Button
                variant="contained"
                onClick={handleAddDesignation}
                disabled={!designation.trim()}
                sx={{ borderRadius: 1, px: 3 }}
              >
                Add
              </Button>
            </Box>
            <Button
              variant="outlined"
              onClick={handleOpenDialog}
              sx={{ borderRadius: 1 }}
            >
              View Designation List
            </Button>
          </Paper>
        </Grid>

        {/* Visitor Time Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="text.primary">
              Visitor Time Settings
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                size="small"
                variant="outlined"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              />
              <TextField
                label="End Time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                size="small"
                variant="outlined"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              />
              <Button
                variant="contained"
                onClick={handleTimeSubmit}
                disabled={!startTime || !endTime}
                sx={{ borderRadius: 1 }}
              >
                Save Time Settings
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Auto Approve Section */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoApprove}
                  onChange={handleAutoApproveChange}
                  color="primary"
                />
              }
              label="Auto Approve Visitors"
              sx={{ color: "text.primary" }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Designation List Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ bgcolor: "primary.main", color: "white", py: 1.5 }}>
          Designation List
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List sx={{ pt: 0 }}>
            {apiDesignationList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No designations available
              </Typography>
            ) : (
              apiDesignationList.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      py: 1,
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() =>
                          handleDeleteDesignation(item.designation)
                        }
                        sx={{
                          color: "red",
                          border: "1px solid red",
                          borderRadius: 1,
                          "&:hover": { bgcolor: "red.50" },
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={item.designation}
                      primaryTypographyProps={{
                        variant: "body1",
                        color: "text.primary",
                      }}
                    />
                  </ListItem>
                  {index < apiDesignationList.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ borderRadius: 1 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Settings;
